import React, { createContext, useContext, useState, useCallback } from 'react'
import { BreadcrumbItem } from '@/components/layout/hero-navbar'

export type HeroVariant = 'pattern' | 'image'

interface HeroNavbarState {
  heading?: React.ReactNode
  variant: HeroVariant
  breadcrumbs: BreadcrumbItem[]
  height?: number
  isActive?: boolean
}

interface HeroNavbarContextValue extends HeroNavbarState {
  setHeading: (h?: React.ReactNode) => void
  setVariant: (v: HeroVariant) => void
  setBreadcrumbs: (b: BreadcrumbItem[]) => void
  setHeight: (h: number) => void
  reset: () => void
  setIsActive: (isActive: boolean) => void
}

const defaultState: HeroNavbarState = {
  heading: 'Dashboard',
  variant: 'pattern',
  breadcrumbs: [],
  isActive: true,
}

const HeroNavbarContext = createContext<HeroNavbarContextValue | null>(null)

export function HeroNavbarProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<HeroNavbarState>(defaultState)

  const setHeading = useCallback((heading?: React.ReactNode) => setState(s => ({ ...s, heading })), [])
  const setVariant = useCallback((variant: HeroVariant) => setState(s => ({ ...s, variant })), [])
  const setBreadcrumbs = useCallback((breadcrumbs: BreadcrumbItem[]) => setState(s => ({ ...s, breadcrumbs })), [])
  const setHeight = useCallback((height: number) => setState(s => ({ ...s, height })), [])
  const setIsActive = useCallback((isActive: boolean) => setState(s => ({ ...s, isActive })), [])
  const reset = useCallback(() => setState(defaultState), [])

  return (
  <HeroNavbarContext.Provider value={{ ...state, setHeading, setVariant, setBreadcrumbs, setHeight, reset, setIsActive }}>
      {children}
    </HeroNavbarContext.Provider>
  )
}

export function useHeroNavbar() {
  const ctx = useContext(HeroNavbarContext)
  if (!ctx) throw new Error('useHeroNavbar must be used within HeroNavbarProvider')
  return ctx
}
